import { Box, Card, Container, SimpleGrid, Stack, Text } from '@mantine/core';

import { MarketingEyebrow } from '@/components/atoms';
import { useInView } from '@/hooks/useInView';
import { T } from '@/theme/assay';

type Feature = {
  /** The lead-in, set in the serif face. */
  title: string;
  /** The payoff. Says what it does for you, not what it is. */
  body: string;
};

type Act = {
  act: string;
  index: string;
  summary: string;
  features: Feature[];
};

// Three acts, nine features - the full surface of the product in the order you
// meet it. Kept in sync with the Fazier launch copy (docs/launch/).
const ACTS: Act[] = [
  {
    act: 'Sort',
    index: '01',
    summary: 'Grade everyone by how close they actually are.',
    features: [
      {
        title: 'Grade your whole network by closeness',
        body: 'Four tiers, each with its own stay-in-touch rhythm. Set it once; Assay does the counting.',
      },
      {
        title: 'Swipe-grade on your phone',
        body: 'Sort hundreds of contacts in one sitting, one card at a time.',
      },
      {
        title: 'Start from what you already have',
        body: 'Import a CSV, connect Google Contacts, or sync the HubSpot you already keep at work.',
      },
    ],
  },
  {
    act: 'Reconnect',
    index: '02',
    summary: 'See who is slipping away, and send something worth reading.',
    features: [
      {
        title: 'A weekly nudge that names names',
        body: 'Exactly who you are due to reconnect with, and who is quietly slipping away - instead of a dashboard you have to interrogate.',
      },
      {
        title: 'Check-ins drafted from your real history',
        body: 'Every call, coffee and note you have logged feeds the draft, so it opens with something real rather than "just checking in".',
      },
      {
        title: 'In your voice, not an AI’s',
        body: 'Set tone, length and greeting style once. Drafts come out sounding like you wrote them, and sharpen as you send.',
      },
    ],
  },
  {
    act: 'Remember',
    index: '03',
    summary: 'Keep a quiet memory of every conversation.',
    features: [
      {
        title: 'Nothing forgotten',
        body: 'Log an interaction in seconds and it is there for the next check-in, years later.',
      },
      {
        title: 'Reads your notes for you',
        body: 'Drop in meeting notes, screenshots or a LinkedIn export and Assay files the details onto the right contacts.',
      },
      {
        title: 'A weekly retro',
        body: 'A digest of what you sent and what came back, which sharpens your templates over time.',
      },
    ],
  },
];

function ActColumn({ act, order, inView }: { act: Act; order: number; inView: boolean }) {
  const base = 0.55 + order * 0.12;

  return (
    <Card
      withBorder
      radius="sm"
      padding="lg"
      style={{
        backgroundColor: 'var(--assay-surface)',
        backgroundImage:
          'radial-gradient(ellipse at 100% 0%, rgba(196, 152, 58, 0.045) 0%, transparent 55%)',
        opacity: 0,
        animation: inView
          ? `assay-word-rise 0.55s cubic-bezier(0.22, 1, 0.36, 1) ${base}s both`
          : undefined,
      }}
    >
      <Stack gap={20}>
        <Stack gap={8}>
          <Text
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: '0.6rem',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: T.ore,
            }}
          >
            <Box component="span" style={{ color: T.textMuted, marginRight: 8 }}>
              {act.index}
            </Box>
            {act.act}
          </Text>
          <Text
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontWeight: 400,
              fontSize: '1.35rem',
              lineHeight: 1.2,
              color: T.textPrimary,
            }}
          >
            {act.summary}
          </Text>
        </Stack>

        <Box
          aria-hidden
          style={{
            height: 1,
            background: `linear-gradient(to right, ${T.ore}55, transparent)`,
            transformOrigin: 'left',
            animation: inView
              ? `assay-eyebrow-rule-draw 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${base + 0.2}s both`
              : undefined,
          }}
        />

        <Stack gap={18} component="ul" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {act.features.map((feature, i) => (
            <Box
              component="li"
              key={feature.title}
              style={{
                opacity: 0,
                animation: inView
                  ? `assay-word-rise 0.5s ease ${base + 0.3 + i * 0.08}s both`
                  : undefined,
              }}
            >
              <Text
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontWeight: 500,
                  fontSize: '1.05rem',
                  lineHeight: 1.3,
                  color: T.textPrimary,
                  marginBottom: 4,
                }}
              >
                {feature.title}
              </Text>
              <Text
                style={{
                  fontFamily: '"DM Mono", monospace',
                  fontSize: '0.75rem',
                  lineHeight: 1.65,
                  color: T.textMuted,
                }}
              >
                {feature.body}
              </Text>
            </Box>
          ))}
        </Stack>
      </Stack>
    </Card>
  );
}

export function FeatureGridSection() {
  const { ref, inView } = useInView(0.1);

  return (
    <Box
      ref={ref}
      component="section"
      aria-labelledby="how-it-works-heading"
      id="how-it-works"
      style={{ scrollMarginTop: 96 }}
    >
      <Container size="lg">
        <Stack gap={40}>
          <Stack gap={16} maw={680}>
            <MarketingEyebrow label="How Assay works" inView={inView} />

            <Box
              component="h2"
              id="how-it-works-heading"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontWeight: 300,
                fontSize: 'clamp(1.9rem, 3.4vw, 2.75rem)',
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
                color: T.textPrimary,
                margin: 0,
                opacity: 0,
                animation: inView ? 'assay-word-rise 0.6s ease 0.45s both' : undefined,
              }}
            >
              Sort, reconnect, remember.
            </Box>

            <Text
              size="sm"
              c="dimmed"
              style={{
                maxWidth: 620,
                opacity: 0,
                animation: inView ? 'assay-word-rise 0.6s ease 0.58s both' : undefined,
              }}
            >
              Three habits, and Assay does the remembering for all of them. No open rates, no
              reply-rate leaderboards - the only metric that matters is whether the people you care
              about are still in your life.
            </Text>
          </Stack>

          <SimpleGrid cols={{ base: 1, sm: 1, md: 3 }} spacing="lg">
            {ACTS.map((act, i) => (
              <ActColumn key={act.act} act={act} order={i} inView={inView} />
            ))}
          </SimpleGrid>
        </Stack>
      </Container>
    </Box>
  );
}
